import { useRouter } from 'next/navigation';
import { createContext, useContext } from 'react';
import { useMemo, useState } from 'react';
import { useEffect } from 'react';

import {
  trackGAEvent,
  trackGAEventWithCommunityAndUser
} from '@/utility/analytics';
import { t } from '@/utility/localization';
import { CM_PORTAL_MONEY_PAGE_ROUTE } from '@/utility/routesHelper';

import { fetchMoneyPageEarnings } from '@/services/moneyPageService';

import { showErrorToast } from '@/components/common/ToastContainer';

import useQueryParams from '@/hooks/useQueryParams';

import useCampaignAnnouncements from '@/pages/portal/hooks/useCampaignAnnouncements';
import {
  getAccessibleMoneyTabs,
  getFormattedRechartConfig,
  getStartEndDateForCurrentDateMonth,
  getStartEndDateFromYearAndMonth
} from '@/pages/portal/money/utils';

import { useAuthContext } from '../AuthContext';
import { useCommunityPortalContext } from '../CommunityPortalContext';

export const MoneyPageContext = createContext();

export const MoneyPageContextProvider = ({ children }) => {
  const { activeCommunity, payoutInfo } = useCommunityPortalContext();
  const { user } = useAuthContext();
  const accessibleMoneyTabs = getAccessibleMoneyTabs(user);
  const router = useRouter();
  const { currentRouterQuery } = useQueryParams();
  const [activeTabId, setActiveTabId] = useState(
    accessibleMoneyTabs.some((tab) => tab.id === currentRouterQuery?.tab)
      ? currentRouterQuery?.tab
      : accessibleMoneyTabs?.[0]?.id
  );
  const [revenueTypesSelected, setRevenueTypesSelected] = useState([]);
  const [earningsCustomDateRange, setEarningsCustomDateRange] = useState(
    getStartEndDateForCurrentDateMonth(new Date())
  );
  const [isLoading, setIsLoading] = useState(false);
  const [earningsData, setEarningsData] = useState(null);
  const [membershipCounts, setMembershipCounts] = useState(null);
  const [monthFilterTooltip, setMonthFilterTooltip] = useState(null);
  const [topSelling, setTopSelling] = useState([]);
  const topSellingPageSize = 10;

  const {
    campaignAnnouncementsSeenData,
    setHaveSeenAnnouncement,
    loading: campaignAnnLoading
  } = useCampaignAnnouncements();

  const { feb2024CampaignRewardReceived, firstDollarCampaign } =
    activeCommunity?.config || {};

  let chartData = useMemo(() => {
    if (!earningsData) return null;
    return getFormattedRechartConfig(earningsData, router);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earningsData]);

  const handleSeeMoreOnetimeRows = () => {
    // add 10 more till all is showing
    setTopSelling((prev) => {
      const newTopSelling = earningsData?.addon?.topSelling?.slice(
        0,
        prev.length + topSellingPageSize
      );
      return newTopSelling;
    });
  };

  // If user has not seen the tooltip, set the tooltip based on the month filter
  const handleGiftTooltip = () => {
    setMonthFilterTooltip(null);

    if (campaignAnnLoading || !campaignAnnouncementsSeenData) return;

    // If community did not receive any rewards, return
    if (!feb2024CampaignRewardReceived && !firstDollarCampaign) return;

    // If user has already seen the tooltip, return
    if (
      campaignAnnouncementsSeenData?.giftTooltip?.includes(
        activeCommunity?._id
      )
    ) {
      return;
    }

    // handle logic for new date formatting (moved away from month/year var to date range for more flexibility)
    const startDate = new Date(earningsCustomDateRange?.startDate);
    if (
      (startDate.getMonth() !== 1 ||
        earningsCustomDateRange.startDate.getFullYear() !== 2024) &&
      feb2024CampaignRewardReceived
    ) {
      trackGAEvent('money_page_gift_tooltip_shown', {
        communityId: activeCommunity?._id,
        month: startDate.getMonth()
      });
      setMonthFilterTooltip(t('select-february-to-view-your-usd-5-gift'));
      return;
    }

    if (
      (startDate.getMonth() !== 0 || startDate.getFullYear() !== 2024) &&
      firstDollarCampaign
    ) {
      trackGAEvent('money_page_gift_tooltip_shown', {
        communityId: activeCommunity?._id,
        month: startDate.getMonth()
      });
      setMonthFilterTooltip(t('select-january-to-view-your-usd-1-gift'));
      return;
    }
  };

  useEffect(() => {
    // If user has not seen the tooltip, set the tooltip based on the month filter
    handleGiftTooltip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    campaignAnnLoading,
    campaignAnnouncementsSeenData,
    campaignAnnouncementsSeenData?.giftTooltip,

    activeCommunity
  ]);

  const fetchEarningsData = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await fetchMoneyPageEarnings(
        activeCommunity?._id,
        {
          ...earningsCustomDateRange,
          addonLimit: 0
        }
      );
      if (error) {
        console.error(error);
        showErrorToast(`Error fetching revenue information: ${error}`);
        return;
      }
      setEarningsData(data);
      setTopSelling(data?.addon?.topSelling?.slice(0, 5));
      setMembershipCounts(data?.totalMembers);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // handle month filter query param - example ?month=1&year=2022
  useEffect(() => {
    const { month, year } = currentRouterQuery;
    // check month is valid between 0-11 and year is valid.
    if (month && year && month >= 0 && month <= 11 && year >= 2023) {
      setEarningsCustomDateRange({
        startDate: getStartEndDateFromYearAndMonth(year, month).startDate,
        endDate: getStartEndDateFromYearAndMonth(year, month).endDate
      });
    }
  }, [currentRouterQuery?.month, currentRouterQuery?.year]);

  // Fetches earnings data when community changes or month/year filter changes
  useEffect(() => {
    if (!activeCommunity?._id) return;
    fetchEarningsData();
  }, [activeCommunity?._id, earningsCustomDateRange]);

  const onChangeMonthFilter = (dateRange) => {
    if (!dateRange?.startDate && !dateRange?.endDate) {
      // all date range use case
      setEarningsCustomDateRange({
        duration: 'all'
      });
      return;
    }
    setEarningsCustomDateRange(dateRange);
  };

  const onStatusTabClick = (id) => {
    const foundTab = getAccessibleMoneyTabs(user).find(
      (tab) => tab.id === id
    );

    if (!foundTab) {
      return;
    }

    trackGAEventWithCommunityAndUser(foundTab.eventName);
    setActiveTabId(id);
    router.replace({
      pathname: CM_PORTAL_MONEY_PAGE_ROUTE,
      query: {
        ...currentRouterQuery,
        tab: id
      }
    });
  };

  const handleCloseMonthFilterTooltip = () => {
    setMonthFilterTooltip(null);
    // handle logic for new date formatting (moved away from month/year var to date range for more flexibility)
    trackGAEvent('money_page_gift_tooltip_closed', {
      communityId: activeCommunity?._id,
      month: new Date(earningsCustomDateRange?.startDate)?.getMonth()
    });
    setHaveSeenAnnouncement('giftTooltip', activeCommunity?._id);
  };

  // Handles switching between tabs
  useEffect(() => {
    const { tab, payoutId, startDate, endDate } = currentRouterQuery;
    // Only trigger this if either one of the queryParams exists
    // this is to prevent unnecessary rerenders
    if ((payoutId || startDate || endDate) && tab) {
      //if the current tab is not accessible, set the first accessible tab as active
      const possibleActiveTabId = accessibleMoneyTabs.some(
        (tab) => tab.id === currentRouterQuery?.tab
      )
        ? tab
        : accessibleMoneyTabs?.[0]?.id;
      setActiveTabId(possibleActiveTabId);
    }
  }, [currentRouterQuery]);

  const value = {
    activeCommunity,
    payoutInfo,
    onStatusTabClick,
    activeTabId,
    setActiveTabId,
    revenueTypesSelected,
    setRevenueTypesSelected,
    earningsCustomDateRange,
    setEarningsCustomDateRange,
    isLoading,
    setIsLoading,
    earningsData,
    onChangeMonthFilter,
    chartData,
    setMembershipCounts,
    membershipCounts,
    handleCloseMonthFilterTooltip,
    monthFilterTooltip,
    topSelling,
    handleSeeMoreOnetimeRows
  };

  return (
    <MoneyPageContext.Provider value={value}>
      {children}
    </MoneyPageContext.Provider>
  );
};

export const useMoneyPageContext = () => useContext(MoneyPageContext);
